import McmPhotoConfig, { McmPhotoType } from '~/tenants/mcm/performable/photo/McmPhotoConfig';
import McmJob from '~/tenants/mcm/model/McmJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { TenantJobLine } from '~/lib/model';
import { Big } from 'big.js';
import { DeliverableType } from '~common/model/Deliverable';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { McmOrderContext } from '~/tenants/mcm/model/McmOrder';
import { mcm_is_crossroads } from '~/tenants/mcm/util';
import { MCM_PHOTOS_BASE_TIERS, MCM_PHOTOS_EXTERIOR_TIERS } from '~/tenants/mcm/data';

export default class McmPhotoJob extends McmJob<typeof McmPhotoConfig> {
  get performable() {
    return McmPhotoConfig;
  }

  get deliverable() {
    return DeliverableType.PHOTO;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  /** Listing photos are shot as bracket sets — the AI-editing product. */
  get aiEditing() {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof McmPhotoConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      type: McmPhotoType.INTERIOR_EXTERIOR,
    };
  }

  revenueLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = [];

    let { name, revenue, range } = this.calculate;

    lines.push({
      id: 'photos',
      description: `${name} (${range} Photos)`,
      amount: revenue,
    });

    if (this.metadata.extra_photos && this.metadata.extra_photos > 0) {
      lines.push({
        id: 'extra_photos',
        description: `Extra Photos (${this.metadata.extra_photos})`,
        amount: McmPhotoJob.calculateExtra(this.metadata.extra_photos),
      });
    }

    return lines;
  }

  static calculateExtra(photos: number) {
    if (photos <= 0) {
      return new Big(0);
    }

    // First tier (1-15 photos): $75, then $50 per additional 15-photo tier
    const tiers = Math.ceil(photos / 15);
    const totalCost = 25 + tiers * 50; // $25 base + $50 per tier

    return new Big(totalCost);
  }

  get minimumPhotos(): number {
    const base = this.calculate.minimum;

    if (this.metadata.extra_photos && this.metadata.extra_photos > 0) {
      return base + this.metadata.extra_photos;
    }

    return base;
  }

  get maximumPhotos(): number {
    const base = this.calculate.maximum;

    if (this.metadata.extra_photos && this.metadata.extra_photos > 0) {
      return base + this.metadata.extra_photos;
    }

    return base;
  }

  get name(): string {
    return this.calculate.name;
  }

  onsite(): number {
    return this.calculate.onsite;
  }

  protected get calculate() {
    return McmPhotoJob.calculatePhotos(this.order, this.metadata.type, Boolean(this.metadata.twilight));
  }

  private static getTierForSqft(sqft: number) {
    // Find the appropriate tier for the given square footage
    for (const tier of MCM_PHOTOS_BASE_TIERS) {
      if (sqft >= tier.min && sqft <= tier.max) {
        return tier;
      }
    }
    // Default to the highest tier if sqft exceeds all ranges
    return MCM_PHOTOS_BASE_TIERS[MCM_PHOTOS_BASE_TIERS.length - 1];
  }

  static calculatePhotos(context: McmOrderContext, type: McmPhotoType, twilight = false) {
    const sqft = context.metadata.sqft || 2000;

    let result = {
      name: 'Photos',
      revenue: new Big(0),
      onsite: 0,
      minimum: 0,
      maximum: 0,
    };

    switch (type) {
      case McmPhotoType.INTERIOR_EXTERIOR:
        const tier = McmPhotoJob.getTierForSqft(sqft);

        result = {
          name: 'Interior + Exterior',
          revenue: new Big(tier.revenue),
          onsite: tier.onsite,
          minimum: tier.minPhotos,
          maximum: tier.maxPhotos,
        };

        // Crossroads package overrides - keep existing logic
        if (mcm_is_crossroads(context, 'platinum')) {
          result.minimum = 60;
          result.revenue = new Big('265');
        } else if (mcm_is_crossroads(context, 'gold')) {
          result.minimum = 50;
          result.revenue = new Big('197.5');
        } else if (mcm_is_crossroads(context, 'silver')) {
          result.minimum = 40;
          result.revenue = new Big('197.5');
        } else if (mcm_is_crossroads(context, 'bronze')) {
          result.minimum = 30;
          result.revenue = new Big('197.50');
        } else if (mcm_is_crossroads(context, 'basic')) {
          result.minimum = 12;
          result.revenue = new Big('100');
        }

        break;
      case McmPhotoType.EXTERIOR_ONLY:
        const exteriorTier =
          MCM_PHOTOS_EXTERIOR_TIERS.find((t) => sqft <= t.max) ||
          MCM_PHOTOS_EXTERIOR_TIERS[MCM_PHOTOS_EXTERIOR_TIERS.length - 1];

        result = {
          name: 'Exterior Only',
          revenue: exteriorTier.price,
          onsite: exteriorTier.onsite,
          minimum: exteriorTier.minPhotos,
          maximum: exteriorTier.maxPhotos,
        };
        break;
      case McmPhotoType.RESHOOT:
        const reshootAdditionalSqft = Math.max(0, sqft - 2000);

        result = {
          name: 'Reshoot',
          revenue: new Big('49.95'),
          onsite: 15 + Math.floor(reshootAdditionalSqft / 1000) * 5,
          minimum: 20,
          maximum: 20,
        };
        break;
      case McmPhotoType.EDITORIAL:
        result = {
          name: 'Editorial',
          revenue: new Big(300),
          onsite: 60,
          maximum: 20,
          minimum: 15,
        };
        break;
      case McmPhotoType.ECONOMY:
        result = {
          name: 'Economy',
          revenue: new Big('179.95'),
          onsite: 20,
          maximum: 15,
          minimum: 0,
        };
        break;
      case McmPhotoType.TWILIGHT:
        result = {
          name: 'Twilight',
          revenue: new Big('249.95'),
          onsite: 60,
          maximum: 20,
          minimum: 10,
        };
    }

    if (twilight) {
      result.name = ' (Daylight + Twilight)';
      result.revenue = result.revenue.plus(new Big('249.95'));
      result.onsite += 60;
      result.minimum += 20;
      result.maximum += 20;
    }

    let range = `Up to ${result.maximum}`;

    if (result.minimum !== result.maximum) {
      range = `${result.minimum}-${result.maximum}`;
    } else if (result.minimum === result.maximum) {
      range = result.minimum.toString();
    }

    return {
      ...result,
      range,
    };
  }

  get configurable() {
    return !this.buyer('crossroads');
  }

  get hidden() {
    return Boolean(this.buyer('crossroads'));
  }
}
